// Previously: apps/editor/components/SidePage/SidePageOptions/PageMetadata.tsx

import { EVENT } from '@/analytics';
import { PageOptionContext, PageOptionDispatchContext } from '@/components/dashboard/context';
import { IPage } from '@typedream/data';
import { Text } from '@typedream/ui';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';
import { useContext, useEffect, useRef, useState } from 'react';
import { PagesOptionFacebook } from './Facebook';
import { PagesOptionSEOSettings } from './SEOSettings';
import { PagesOptionTwitter } from './Twitter';
import { PageMetadataTab, PagesOptionMetadataProps } from './types';

export function PagesOptionMetadata({
  metadataTab,
  upgradeRequired = false,
  onMetadataChange,
  imageChangeStatus,
  setImageChangeStatus,
}: PagesOptionMetadataProps) {
  const { space, page, meta, twitter, twitterImage, facebook, facebookImage } =
    useContext(PageOptionContext);
  const { setTwitter, setFacebook, setTwitterImage, setFacebookImage } =
    useContext(PageOptionDispatchContext);

  const [dataChangedStatus, setDataChangedStatus] = useState(false);
  const dataChangedStatusRef = useRef(dataChangedStatus);

  useEffect(() => {
    dataChangedStatusRef.current = dataChangedStatus;
  }, [dataChangedStatus]);

  const onRemoveCard = () => {
    setTwitter((curr) => ({ ...curr, imageURL: '' }));
    setTwitterImage(null);
    setFacebook((curr) => ({ ...curr, imageURL: '' }));
    setFacebookImage(null);
  };

  useEffect(() => {
    if (metadataTab !== PageMetadataTab.DEFAULT) {
      mixpanel.track(EVENT.OPEN_SEO, {
        seo_entry_point: 'page_settings',
      });
    }
  }, [metadataTab]);

  const hasDataChanged = (_page: IPage) => {
    if (_page) {
      return (
        (page.metadata?.metatags?.title || '') !== meta.title ||
        (page.metadata?.metatags?.description || '') !== meta.description ||
        (page.metadata?.twitter?.title || '') !== twitter.title ||
        (page.metadata?.twitter?.description || '') !== twitter.description ||
        (page.metadata?.twitter?.username || space.metadata?.twitter?.username || '') !==
          twitter.username ||
        (page.metadata?.facebook?.title || '') !== facebook.title ||
        (page.metadata?.facebook?.description || '') !== facebook.description ||
        imageChangeStatus
      );
    }
    return false;
  };

  useEffect(() => {
    onMetadataChange(hasDataChanged(page));
    setDataChangedStatus(hasDataChanged(page));
  }, [
    meta.title,
    meta.description,
    twitter.title,
    twitter.description,
    twitter.username,
    facebook.title,
    facebook.description,
    imageChangeStatus,
    page?.metadata,
  ]);

  useEffect(() => {
    setImageChangeStatus(
      ((page?.metadata?.twitter?.image || '') && !twitter.imageURL) ||
        !!twitterImage ||
        ((page?.metadata?.facebook?.image || '') && !facebook.imageURL) ||
        !!facebookImage
    );
  }, [facebook.imageURL, twitter.imageURL, twitterImage, facebookImage]);

  const ref = useRef(null);
  // const onClickOutside = () => {
  //   if (dataChangedStatusRef.current) {
  //     setIsShowSaveWarningModal(true);
  //   }
  // };
  // useOutsideAlerter(ref, null, onClickOutside);

  return (
    <div ref={ref} className="w-[500px] px-5">
      {metadataTab === PageMetadataTab.METADATA && (
        <div className="py-5 px-0">
          <div
            className={classNames('py-2.5 px-0', {
              'opacity-30 pointer-events-none': upgradeRequired,
            })}
          >
            <Text as="p" size="md" className="mb-2">
              Meta data
            </Text>
            <Text as="p" size="sm" variant="secondary" weight="medium" className="mb-5">
              Extra content for search engines
              <a
                href="https://typedream.com/blog/seo-tips#title-tag-meta-description"
                target="_blank"
                className="is-block is-size-7"
                rel="noreferrer"
              >
                Read more about Metadata →
              </a>
            </Text>
            <PagesOptionSEOSettings columns={false} disabled={upgradeRequired} />
          </div>
        </div>
      )}

      {metadataTab === PageMetadataTab.TWITTER && (
        <div className="py-5 px-0">
          <div
            className={classNames('py-2.5 px-0', {
              'opacity-30 pointer-events-none': upgradeRequired,
            })}
          >
            <Text as="p" size="md" className="mb-2">
              Twitter Card
            </Text>
            <Text as="p" size="sm" variant="secondary" weight="medium" className="mb-5">
              Customize how your site looks like when shared on Twitter
            </Text>
            <PagesOptionTwitter
              columns={false}
              disabled={upgradeRequired}
              onRemoveCard={onRemoveCard}
            />
          </div>
        </div>
      )}

      {metadataTab === PageMetadataTab.FACEBOOK && (
        <div className="py-5 px-0">
          <div
            className={classNames('py-2.5 px-0', {
              'opacity-30 pointer-events-none': upgradeRequired,
            })}
          >
            <Text as="p" size="md" className="mb-2">
              Facebook Card
            </Text>
            <Text as="p" size="sm" variant="secondary" weight="medium" className="mb-5">
              Customize how your site looks like when shared on Facebook
            </Text>
            <PagesOptionFacebook
              columns={false}
              disabled={upgradeRequired}
              onRemoveCard={onRemoveCard}
            />
          </div>
        </div>
      )}
      {/* <SettingsSaveWarningPopup
        isShow={isShowSaveWarningModal}
        handleBtnClick={() => {
          onSave(metadataTab);
        }}
        setIsShow={setIsShowSaveWarningModal}
      /> */}
    </div>
  );
}
